import type * as React from "react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  createContext,
  useCallback,
} from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { AuthContext, AUTHENTICATED } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { BoxyList, BoxyListItem, TextInput } from "@bfc-software/pallet";

import styles from "./command-palette.module.css";
import { type Command, navigationCommand, getIconForKind } from "./command";
import { useGlobalHotkeys } from "./global-keys";

type CommandPaletteContextValue = {
  open: () => void;
  close: () => void;
  toggle: () => void;
  register: (cmds: Command[]) => () => void; // returns cleanup
};

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(
  null,
);

function emptyList(t: TFunction) {
  return (
    <BoxyListItem
      textValue={t("global.commandPalette.noResults")}
      disabled={true}
      icon="Square"
    >
      <span>{t("global.commandPalette.noResults")}</span>
    </BoxyListItem>
  );
}

type CommandEntryContext = {
  t: TFunction;
  activeIndex: number;
  run: (c: Command) => void;
};

function commandEntry(
  command: Command,
  ctx: CommandEntryContext,
  currentIndex: number,
) {
  const icon = getIconForKind(command.kind);

  return (
    <BoxyListItem
      onClick={() => ctx.run(command)}
      key={command.id}
      icon={icon}
      active={currentIndex == ctx.activeIndex}
      textValue={ctx.t(command.title)}
    >
      <span>{ctx.t(command.title)}</span>
    </BoxyListItem>
  );
}

export function useCommandPalette() {
  const ctx = useContext(CommandPaletteContext);

  if (!ctx)
    throw new Error(
      "useCommandPalette must be used within CommandPaletteProvider",
    );

  return ctx;
}

function normalize(s: string) {
  return s.trim().toLowerCase();
}

// Returns true if our command should be displayed in the list, given some user input string.
function matchCommand(cmd: Command, q: string) {
  const tokens = normalize(q).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;

  const haystack = normalize([cmd.title, ...(cmd.keywords ?? [])].join(" "));

  return tokens.every((t) => haystack.includes(t));
}

export function CommandPaletteProvider({
  children,
  initialCommands,
}: {
  children: React.ReactNode;
  initialCommands?: Readonly<Command[]>;
}) {
  const { lang } = useParams({ strict: false });
  const { state } = useContext(AuthContext);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { t } = useTranslation({
    lang: lang ?? "",
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const navigate = useNavigate();

  const run = useCallback(
    (command: Command) => {
      setIsOpen(false);
      const { kind: commandKind } = command;

      switch (commandKind.kind) {
        case "navigation":
          navigate(commandKind.to());
          return;
      }
    },
    [navigate, setIsOpen],
  );

  const registryRef = useRef<Map<string, Command>>(new Map());

  useEffect(() => {
    for (const cmd of initialCommands ?? []) {
      registryRef.current.set(cmd.id, cmd);
    }
  }, [initialCommands]);

  const allCommands = useMemo(() => {
    return isOpen ? Array.from(registryRef.current.values()) : [];
  }, [isOpen]);

  const filtered = useMemo(
    () => allCommands.filter((c) => matchCommand(c, query)),
    [allCommands, query],
  );

  useGlobalHotkeys({
    enabled: true,
    onOpen: open,
    onClose: close,
  });

  // Resets the query and active command index when we re-open the palette.
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setActiveIndex(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  // Make sure we stay within bounds of the max available commands.
  useEffect(() => {
    setActiveIndex((i) =>
      Math.max(0, Math.min(i, Math.max(0, filtered.length - 1))),
    );
  }, [filtered.length]);

  const register = useCallback((cmds: Command[]) => {
    for (const c of cmds) registryRef.current.set(c.id, c);
    return () => {
      for (const c of cmds) registryRef.current.delete(c.id);
    };
  }, []);

  const ctxValue = useMemo<CommandPaletteContextValue>(
    () => ({ open, close, toggle, register }),
    [open, close, toggle, register],
  );

  if (!isOpen || state.status !== AUTHENTICATED) {
    return (
      <CommandPaletteContext.Provider value={ctxValue}>
        {children}
      </CommandPaletteContext.Provider>
    );
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = filtered[activeIndex];
      if (cmd) void run(cmd);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  const entryCtx: CommandEntryContext = {
    activeIndex,
    t,
    run,
  };

  return (
    <CommandPaletteContext.Provider value={ctxValue}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => {
          // click outside to close
          if (e.target === e.currentTarget) close();
        }}
        className={styles.paletteModal}
      >
        <div className={styles.paletteInner} onKeyDown={onKeyDown}>
          {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
          <div style={{ padding: 12, borderBottom: "1px solid #eee" }}>
            <TextInput
              name={t("global.commandPalette.search")}
              autofocus={isOpen}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("global.commandPalette.search")}
              aria-label={t("global.commandPalette.search")}
            />
            {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
            <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
              {t("global.commandPalette.tip")}
            </div>
          </div>
          <BoxyList label={t("global.commandPalette.listLabel")}>
            {filtered.length > 0
              ? filtered.map((command, index) =>
                  commandEntry(command, entryCtx, index),
                )
              : emptyList(t)}
          </BoxyList>
        </div>
      </div>
      <>{children}</>
    </CommandPaletteContext.Provider>
  );
}

export { navigationCommand, type Command };
